'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { CheckCircleIcon, XMarkIcon, ArrowRightIcon, BuildingOffice2Icon, ClockIcon, HomeModernIcon, PencilSquareIcon, CheckIcon, MapPinIcon, ClipboardDocumentListIcon, PhoneArrowDownLeftIcon, FolderOpenIcon, ShoppingBagIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  desc: string;
  badge?: string;
  features: { label: string; included: boolean }[];
}

interface Product {
  name: string;
  tagline: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  badge?: string;
  images?: { src: string; alt: string }[];
}

const products: Product[] = [
  {
    name: 'MANG ASSET',
    tagline: 'Smart Asset Management',
    description: 'A comprehensive asset management solution designed to help businesses track, manage, and optimise their physical and digital assets throughout their entire lifecycle.',
    icon: <BuildingOffice2Icon className="w-8 h-8" />,
    color: '#00C9A7',
    badge: 'Asset Management',
    images: [
      { src: '/assets/images/5D51921B-BF56-4235-85C8-C6413BEEA139-1786611963708.PNG', alt: 'MANG ASSET dashboard showing asset tracking and lifecycle management interface' },
    ],
    features: [
      'Asset tracking & lifecycle management',
      'Maintenance scheduling & alerts',
      'Depreciation & valuation reports',
      'QR code & barcode scanning',
      'Real-time asset location tracking',
      'Compliance & audit reporting',
    ],
  },
  {
    name: 'ENYANDENI CLOCKING',
    tagline: 'Workforce Time & Attendance',
    description: 'A powerful clocking and workforce management system built for South African businesses. Track employee attendance, manage shifts, and ensure accurate payroll processing with ease.',
    icon: <ClockIcon className="w-8 h-8" />,
    color: '#3B82F6',
    badge: 'HR & Attendance',
    images: [
      { src: '/assets/images/D7DF6F62-6F81-4606-A9F5-901D11D8A357-1786611964568.PNG', alt: 'ENYANDENI CLOCKING system showing employee attendance and clocking interface' },
      { src: '/assets/images/IMG_3049-1786611936183.jpg', alt: 'ENYANDENI CLOCKING workforce management dashboard with shift scheduling' },
      { src: '/assets/images/IMG_3111-1786611960275.PNG', alt: 'ENYANDENI CLOCKING reporting screen showing payroll and overtime tracking' },
    ],
    features: [
      'Biometric & PIN-based clocking',
      'Shift scheduling & management',
      'Overtime & leave tracking',
      'Payroll integration ready',
      'Multi-site & remote clocking',
      'BCEA compliance reporting',
    ],
  },
  {
    name: 'PROP FLOW',
    tagline: 'Property Management Simplified',
    description: 'An end-to-end property management platform for landlords, agents, and property managers. Streamline tenant management, lease tracking, maintenance requests, and financial reporting.',
    icon: <HomeModernIcon className="w-8 h-8" />,
    color: '#F59E0B',
    badge: 'Property Management',
    images: [
      { src: '/assets/images/2272B5C0-879A-47CF-B199-F3678C521BC5-1786611964542.PNG', alt: 'PROP FLOW property management ERP showing tenant and lease management dashboard' },
    ],
    features: [
      'Tenant & lease management',
      'Rental invoicing & payments',
      'Maintenance request tracking',
      'Vacancy & listing management',
      'Financial reporting & statements',
      'Document storage & e-signatures',
    ],
  },
  {
    name: 'CemeteryMS',
    tagline: 'Cemetery Management System',
    description: 'A dedicated cemetery management solution for municipalities, religious organisations, and private operators. Digitise grave records, manage burials, and provide families with easy access to burial information.',
    icon: <MapPinIcon className="w-8 h-8" />,
    color: '#6366F1',
    badge: 'Cemetery Management',
    features: [
      'Digital grave & burial records',
      'Interment scheduling & management',
      'Family & next-of-kin portal',
      'Plot availability & mapping',
      'Death certificate integration',
      'Reporting & compliance tools',
    ],
  },
  {
    name: 'Gate Register',
    tagline: 'Smart Visitor & Access Management',
    description: 'A digital gate register system for managing visitor access, contractor entries, and vehicle movements at facilities, estates, and municipal premises with full audit trails.',
    icon: <ClipboardDocumentListIcon className="w-8 h-8" />,
    color: '#EF4444',
    badge: 'Access Management',
    features: [
      'Digital visitor sign-in & sign-out',
      'Contractor & vehicle management',
      'Pre-authorised visitor invites',
      'Real-time access notifications',
      'Photo ID capture & verification',
      'Full audit trail & reporting',
    ],
  },
  {
    name: 'Municipal Customer Care',
    tagline: 'Citizen Service & Query Management',
    description: 'A comprehensive customer care platform built for municipalities to manage citizen queries, complaints, and service requests efficiently — improving service delivery and accountability.',
    icon: <PhoneArrowDownLeftIcon className="w-8 h-8" />,
    color: '#10B981',
    badge: 'Municipal Services',
    features: [
      'Citizen query & complaint logging',
      'Service request tracking & escalation',
      'Multi-channel intake (walk-in, phone, online)',
      'SLA monitoring & alerts',
      'Department routing & assignment',
      'Performance & resolution reporting',
    ],
  },
  {
    name: 'EDMS',
    tagline: 'Electronic Document Management',
    description: 'An enterprise-grade Electronic Document Management System (EDMS) for capturing, storing, organising, and retrieving documents securely — reducing paper dependency and improving compliance.',
    icon: <FolderOpenIcon className="w-8 h-8" />,
    color: '#8B5CF6',
    badge: 'Document Management',
    features: [
      'Document capture & digitisation',
      'Version control & audit history',
      'Role-based access & permissions',
      'Advanced search & metadata tagging',
      'Workflow automation & approvals',
      'Compliance & retention management',
    ],
  },
  {
    name: 'Store Management',
    tagline: 'Inventory & Retail Operations',
    description: 'A full-featured store and inventory management system for retail, wholesale, and municipal stores — streamlining stock control, procurement, and sales operations in one platform.',
    icon: <ShoppingBagIcon className="w-8 h-8" />,
    color: '#F97316',
    badge: 'Store & Inventory',
    images: [
      { src: '/assets/images/IMG_3870-1786612332641.jpg', alt: 'Store Management POS system showing point-of-sale interface and inventory dashboard' },
    ],
    features: [
      'Real-time stock tracking & alerts',
      'Purchase orders & supplier management',
      'Barcode & QR code scanning',
      'Sales & point-of-sale integration',
      'Stock take & reconciliation',
      'Reporting & analytics dashboard',
    ],
  },
  {
    name: 'Company Registrations',
    tagline: 'Business & Administration Services',
    description: 'Professional business registration and administration services to help entrepreneurs and businesses get legally compliant — from company registration to statutory compliance and documentation.',
    icon: <PencilSquareIcon className="w-8 h-8" />,
    color: '#6366F1',
    badge: 'Business Services',
    images: [
      { src: '/assets/images/IMG_3783-1786612336585.jpg', alt: 'Business and Administration services flyer showing company registration packages and pricing' },
    ],
    features: [
      'Company registration (CIPC)',
      'Tax number & VAT registration',
      'Business bank account assistance',
      'Statutory compliance support',
      'BEE certificate facilitation',
      'Annual returns & amendments',
    ],
  },
  {
    name: 'Website & App Development',
    tagline: 'Custom Digital Solutions',
    description: 'End-to-end website and mobile application development tailored for South African businesses — from professional business websites to full-featured e-commerce and custom web applications.',
    icon: <FolderOpenIcon className="w-8 h-8" />,
    color: '#00C9A7',
    badge: 'Digital Development',
    images: [
      { src: '/assets/images/ChatGPT_Image_Aug_13__2026__11_38_11_AM-1786614063190.png', alt: 'MANGQASHA IT SOLUTIONS Website & App Development flyer showing web and mobile development packages' },
    ],
    features: [
      'Professional business websites',
      'E-commerce & online store setup',
      'Mobile app development (iOS & Android)',
      'Custom web application development',
      'SEO & digital marketing integration',
      'Ongoing maintenance & support',
    ],
  },
];

function ProductImageSlideshow({ images, color }: { images: { src: string; alt: string }[]; color: string }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [images.length]);

  const prev = () => setCurrent((c) => (c - 1 + images.length) % images.length);
  const next = () => setCurrent((c) => (c + 1) % images.length);

  return (
    <div className="relative w-full h-44 rounded-xl overflow-hidden mb-5 group">
      <Image
        src={images[current].src}
        alt={images[current].alt}
        fill
        className="object-cover transition-opacity duration-500"
        sizes="(max-width: 768px) 100vw, 33vw"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            aria-label="Previous image"
          >
            <ChevronLeftIcon className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            aria-label="Next image"
          >
            <ChevronRightIcon className="w-4 h-4 text-white" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{ backgroundColor: i === current ? color : 'rgba(255,255,255,0.4)' }}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const defaultPlans: Plan[] = [
  {
    name: 'Starter',
    monthlyPrice: 299,
    annualPrice: 249,
    desc: 'For businesses with up to 50 employees getting started with ERP.',
    features: [
      { label: 'Finance & Accounting', included: true },
      { label: 'Inventory Management', included: true },
      { label: 'HR & Payroll', included: true },
      { label: 'CRM & Sales', included: false },
      { label: 'Supply Chain', included: false },
      { label: 'Manufacturing', included: false },
      { label: 'Social Selling (FB + IG)', included: false },
      { label: '5 user seats', included: true },
      { label: 'Standard reports', included: true },
      { label: 'Email support', included: true },
      { label: 'Priority support', included: false },
      { label: 'Dedicated CSM', included: false },
      { label: 'Custom integrations', included: false },
      { label: 'SLA guarantee', included: false },
    ],
  },
  {
    name: 'Professional',
    monthlyPrice: 699,
    annualPrice: 579,
    desc: 'For growing teams up to 200 employees needing full ERP capabilities.',
    badge: 'Most Popular',
    features: [
      { label: 'Finance & Accounting', included: true },
      { label: 'Inventory Management', included: true },
      { label: 'HR & Payroll', included: true },
      { label: 'CRM & Sales', included: true },
      { label: 'Supply Chain', included: true },
      { label: 'Manufacturing', included: false },
      { label: 'Social Selling (FB + IG)', included: true },
      { label: '25 user seats', included: true },
      { label: 'Advanced analytics', included: true },
      { label: 'Email support', included: true },
      { label: 'Priority support', included: true },
      { label: 'Dedicated CSM', included: false },
      { label: 'Custom integrations', included: false },
      { label: 'SLA guarantee', included: false },
    ],
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    annualPrice: null,
    desc: 'For large organizations with complex, multi-entity requirements.',
    features: [
      { label: 'Finance & Accounting', included: true },
      { label: 'Inventory Management', included: true },
      { label: 'HR & Payroll', included: true },
      { label: 'CRM & Sales', included: true },
      { label: 'Supply Chain', included: true },
      { label: 'Manufacturing', included: true },
      { label: 'Social Selling (FB + IG)', included: true },
      { label: 'Unlimited users', included: true },
      { label: 'Custom reporting', included: true },
      { label: 'Email support', included: true },
      { label: 'Priority support', included: true },
      { label: 'Dedicated CSM', included: true },
      { label: 'Custom integrations', included: true },
      { label: 'SLA guarantee', included: true },
    ],
  },
];

const faqs = [
  { q: 'How long does ERP implementation take?', a: 'Starter implementations typically take 2–4 weeks. Professional takes 4–8 weeks. Enterprise timelines are scoped during the sales process, typically 8–16 weeks.' },
  { q: 'Can I migrate data from my existing system?', a: 'Yes. We provide data migration tools and a dedicated migration specialist for Professional and Enterprise plans. Starter includes self-service migration templates.' },
  { q: 'Is training included?', a: 'All plans include onboarding training. Professional and Enterprise include live training sessions and access to our certification program.' },
  { q: 'What industries does MANGQASHA IT SOLUTIONS support?', a: 'MANGQASHA IT SOLUTIONS is used across manufacturing, retail, distribution, professional services, healthcare, and more. Industry-specific templates are available for all plans.' },
  { q: 'Can I add more user seats later?', a: 'Yes, additional seats can be added at any time. Pricing is prorated for the remainder of your billing period.' },
];

export default function ProductsPage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [editingPrices, setEditingPrices] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [draftPlans, setDraftPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansSaving, setPlansSaving] = useState(false);

  const supabase = createClient();

  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const { data, error } = await supabase
          .from('erp_plans')
          .select('*')
          .order('sort_order', { ascending: true });

        if (error || !data) {
          setPlansLoading(false);
          return;
        }

        const mapped: Plan[] = data.map((row: any) => ({
          id: row.id,
          name: row.name,
          monthlyPrice: row.monthly_price !== null ? Number(row.monthly_price) : null,
          annualPrice: row.annual_price !== null ? Number(row.annual_price) : null,
          desc: row.description,
          badge: row.badge || undefined,
          features: Array.isArray(row.features) ? row.features : [],
        }));

        setPlans(mapped);
        setDraftPlans(mapped.map((p) => ({ ...p })));
      } catch {
        // silently fall through — page still renders with empty plans
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleStartEdit = () => {
    setDraftPlans(plans.map((p) => ({ ...p })));
    setEditingPrices(true);
  };

  const handleSavePrices = async () => {
    setPlansSaving(true);
    try {
      for (const plan of draftPlans) {
        await supabase
          .from('erp_plans')
          .update({
            monthly_price: plan.monthlyPrice,
            annual_price: plan.annualPrice,
            updated_at: new Date().toISOString(),
          })
          .eq('id', plan.id);
      }
      setPlans(draftPlans.map((p) => ({ ...p })));
      setEditingPrices(false);
    } catch {
      // silently ignore
    } finally {
      setPlansSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDraftPlans(plans.map((p) => ({ ...p })));
    setEditingPrices(false);
  };

  const updateDraftPrice = (index: number, field: 'monthlyPrice' | 'annualPrice', value: string) => {
    const parsed = value === '' ? null : Number(value);
    setDraftPlans((prev) =>
      prev.map((p, i) => i === index ? { ...p, [field]: isNaN(parsed as number) ? p[field] : parsed } : p)
    );
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D]">
      <Navbar />

      {/* Our Products Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <p className="section-label mb-3">Our Products</p>
          <h1 className="text-4xl font-extrabold text-white mb-4">Built for South African Businesses</h1>
          <p className="text-[#94A3B8] text-base max-w-2xl mx-auto">
            MANGQASHA IT SOLUTIONS delivers purpose-built software products that solve real operational challenges — from asset tracking to workforce management and property administration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product) => (
            <div
              key={product.name}
              className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6 flex flex-col hover:border-opacity-60 transition-all duration-300"
              style={{ borderColor: `${product.color}30` }}
            >
              {product.images && product.images.length > 0 && (
                <ProductImageSlideshow images={product.images} color={product.color} />
              )}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${product.color}15`, color: product.color }}
                >
                  {product.icon}
                </div>
                <span
                  className="text-xs font-semibold px-3 py-1 rounded-full"
                  style={{ backgroundColor: `${product.color}15`, color: product.color }}
                >
                  {product.badge}
                </span>
              </div>
              <h2 className="text-xl font-extrabold text-white mb-1">{product.name}</h2>
              <p className="text-sm font-medium mb-3" style={{ color: product.color }}>{product.tagline}</p>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">{product.description}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <CheckCircleIcon className="w-4 h-4 shrink-0" style={{ color: product.color }} />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                href="/book-demo"
                className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors text-[#0F1B2D]"
                style={{ backgroundColor: product.color }}
              >
                Book a Demo <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-t border-[#1E3352]" />
      </div>

      {/* ERP Plans Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <p className="section-label mb-3">ERP Plans</p>
        <h2 className="text-4xl font-extrabold text-white mb-4">Choose the right ERP for your business</h2>
        <p className="text-[#94A3B8] text-base max-w-xl mx-auto mb-8">
          All plans include a 30-day free trial, data migration support, and onboarding training. No credit card required to start.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="inline-flex items-center gap-3 bg-[#162236] border border-[#1E3352] rounded-xl p-1.5">
            <button
              onClick={() => setAnnual(false)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${!annual ? 'bg-[#00C9A7] text-[#0F1B2D]' : 'text-[#94A3B8] hover:text-white'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${annual ? 'bg-[#00C9A7] text-[#0F1B2D]' : 'text-[#94A3B8] hover:text-white'}`}
            >
              Annual <span className="text-xs ml-1 opacity-80">Save 17%</span>
            </button>
          </div>

          {!editingPrices ? (
            <button
              onClick={handleStartEdit}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#162236] border border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7] hover:text-white text-sm font-semibold transition-colors"
            >
              <PencilSquareIcon className="w-4 h-4" />
              Edit Prices
            </button>
          ) : (
            <div className="inline-flex items-center gap-2">
              <button
                onClick={handleSavePrices}
                disabled={plansSaving}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#00C9A7] text-[#0F1B2D] text-sm font-semibold transition-colors hover:bg-[#00B896] disabled:opacity-60"
              >
                <CheckIcon className="w-4 h-4" />
                {plansSaving ? 'Saving…' : 'Save Prices'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#162236] border border-[#1E3352] text-[#94A3B8] hover:text-white text-sm font-semibold transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Cancel
              </button>
            </div>
          )}
        </div>

        {editingPrices && (
          <p className="mt-3 text-xs text-[#64748B]">Edit the monthly and annual prices for each plan below, then click Save Prices. Changes are saved to the store management system.</p>
        )}
      </section>

      {/* Plans */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {plansLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-[#64748B]">
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading plans…</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(editingPrices ? draftPlans : plans).map((plan, index) => {
              const price = annual ? plan.annualPrice : plan.monthlyPrice;
              return (
                <div
                  key={plan.id}
                  className={`rounded-2xl p-6 border relative ${
                    plan.badge
                      ? 'bg-[#00C9A7]/5 border-[#00C9A7]/40'
                      : 'bg-[#162236] border-[#1E3352]'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00C9A7] text-[#0F1B2D] text-xs font-bold px-4 py-1 rounded-full">
                      {plan.badge}
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-white mb-1">{plan.name}</h2>
                  <p className="text-xs text-[#64748B] mb-4">{plan.desc}</p>

                  {editingPrices && plan.name !== 'Enterprise' ? (
                    <div className="mb-6 space-y-3">
                      <div>
                        <label className="block text-xs text-[#64748B] mb-1">Monthly Price (R)</label>
                        <div className="flex items-center gap-1 bg-[#0F1B2D] border border-[#1E3352] rounded-lg px-3 py-2 focus-within:border-[#00C9A7] transition-colors">
                          <span className="text-[#94A3B8] text-sm font-semibold">R</span>
                          <input
                            type="number"
                            min="0"
                            value={draftPlans[index]?.monthlyPrice ?? ''}
                            onChange={(e) => updateDraftPrice(index, 'monthlyPrice', e.target.value)}
                            className="flex-1 bg-transparent text-white text-sm font-semibold outline-none"
                            placeholder="0"
                          />
                          <span className="text-[#64748B] text-xs">/mo</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[#64748B] mb-1">Annual Price (R)</label>
                        <div className="flex items-center gap-1 bg-[#0F1B2D] border border-[#1E3352] rounded-lg px-3 py-2 focus-within:border-[#00C9A7] transition-colors">
                          <span className="text-[#94A3B8] text-sm font-semibold">R</span>
                          <input
                            type="number"
                            min="0"
                            value={draftPlans[index]?.annualPrice ?? ''}
                            onChange={(e) => updateDraftPrice(index, 'annualPrice', e.target.value)}
                            className="flex-1 bg-transparent text-white text-sm font-semibold outline-none"
                            placeholder="0"
                          />
                          <span className="text-[#64748B] text-xs">/mo</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-6">
                      {price !== null && price !== undefined ? (
                        <>
                          <span className="text-4xl font-extrabold text-white">R{price.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span className="text-sm text-[#64748B]">/mo</span>
                        </>
                      ) : (
                        <span className="text-3xl font-extrabold text-white">Custom</span>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-2 mb-6">
                    <Link
                      href="/book-demo"
                      className={`text-center py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        plan.badge
                          ? 'bg-[#00C9A7] text-[#0F1B2D] hover:bg-[#00B896]'
                          : 'border border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7] hover:text-white'
                      }`}
                    >
                      Book Demo
                    </Link>
                    <Link
                      href="/store"
                      className="text-center py-2.5 rounded-lg text-sm font-semibold border border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7] hover:text-white transition-colors"
                    >
                      {plan.name === 'Enterprise' ? 'Contact Sales' : 'Add to Cart'}
                    </Link>
                  </div>
                  <ul className="space-y-2.5">
                    {plan.features?.map((f: { label: string; included: boolean }) => (
                      <li key={f.label} className="flex items-center gap-2 text-sm">
                        {f.included ? (
                          <CheckCircleIcon className="w-4 h-4 text-[#00C9A7] shrink-0" />
                        ) : (
                          <XMarkIcon className="w-4 h-4 text-[#1E3352] shrink-0" />
                        )}
                        <span className={f.included ? 'text-[#94A3B8]' : 'text-[#3D5068]'}>{f.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-2xl font-extrabold text-white mb-8 text-center">ERP Implementation FAQ</h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#162236] border border-[#1E3352] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-sm font-semibold text-white">{faq.q}</span>
                <ArrowRightIcon className={`w-4 h-4 text-[#64748B] shrink-0 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
